import { useMutation } from "@apollo/client";
import { PlusIcon } from "@heroicons/react/outline";
import { useState } from "react";
import {
  DeleteGoalKeyResultsMutation,
  DeleteGoalKeyResultsMutationVariables,
  GoalArtifactSidebarFragmentFragment,
  KeyResultFragmentFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { artifactEmbedCardClassName } from "@components/wysiwyg/extensions/artifacts/artifact-embed-card";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import deleteGoalKeyResultMutation from "../graphql/delete-key-result-mutation";
import getArtifactActivitiesQuery from "../graphql/get-artifact-activities-query";
import GoalKeyResultItem from "./goal-key-result-item";
import GoalKeyResultModal from "./goal-key-result-modal";

const GoalKeyResultList = ({
  goalArtifact,
  isReadOnly = false,
  variant = "sidebar",
}: {
  goalArtifact: GoalArtifactSidebarFragmentFragment;
  isReadOnly?: boolean;
  variant?: "sidebar" | "page";
}) => {
  const label = useLabel();
  const { confirm, ConfirmationDialog } = useConfirm(
    `Delete ${label("key result")}?`,
    `Are you sure you want to delete this ${label("key result")}?`,
  );
  const [deleteKeyResult] = useMutation<
    DeleteGoalKeyResultsMutation,
    DeleteGoalKeyResultsMutationVariables
  >(deleteGoalKeyResultMutation);
  const [showCreateKeyResultModal, setShowCreateKeyResultModal] =
    useState(false);
  const [editingKeyResult, setEditingKeyResult] =
    useState<KeyResultFragmentFragment | null>(null);

  const handleDeleteKeyResult = async (
    keyResult: KeyResultFragmentFragment,
  ) => {
    if (!(await confirm())) return;
    const newKeyResults = goalArtifact.keyResults?.edges.filter(
      (edge) => edge?.node?.id !== keyResult.id,
    );
    deleteKeyResult({
      variables: {
        keyResultId: keyResult.id,
      },
      optimisticResponse: {
        __typename: "Mutation",
        deleteKeyResult: {
          __typename: "DeleteKeyResultMutation",
          keyResult,
          goal: {
            ...goalArtifact,
            keyResults: {
              ...goalArtifact.keyResults,
              edges: newKeyResults || [],
            },
          },
        },
      },
      refetchQueries: [getArtifactActivitiesQuery],
    });
  };

  if (
    goalArtifact.keyResults?.edges.length === 0 &&
    !goalArtifact.canUpdate.permission
  ) {
    return null;
  }

  const keyResults = assertEdgesNonNull(goalArtifact.keyResults);

  if (keyResults.length === 0 && isReadOnly) {
    return null;
  }
  const title = label("key result", { pluralize: true, capitalize: true });

  const action =
    !isReadOnly && goalArtifact.canUpdate.permission ? (
      <div className="flex items-center gap-2">
        {showCreateKeyResultModal && (
          <GoalKeyResultModal
            goalArtifact={goalArtifact}
            onClose={() => setShowCreateKeyResultModal(false)}
          />
        )}
        <Button
          icon={PlusIcon}
          theme={buttonTheme.iconGray}
          onClick={() => setShowCreateKeyResultModal(true)}
          tooltip={`Add ${label("key result")}`}
          aria-label={`Add ${label("key result")}`}
        />
      </div>
    ) : undefined;

  const canEdit = !isReadOnly && goalArtifact.canUpdate.permission;

  const items = (
    <div className="empty:hidden flex flex-col gap-1.5">
      {keyResults.map((keyResult) =>
        variant === "page" ? (
          // Same card chrome as the goal items in the alignment section /
          // meeting-note embeds. Clicking the card opens the edit modal.
          <div
            key={keyResult.id}
            className={classNames(
              artifactEmbedCardClassName,
              "relative",
              canEdit && "hover:bg-gray-100",
            )}
          >
            {canEdit && (
              <button
                type="button"
                aria-label={`Edit ${label("key result")}: ${keyResult.title}`}
                className="absolute inset-0 rounded-md cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
                onClick={() => setEditingKeyResult(keyResult)}
              />
            )}
            <div
              className={classNames(
                "flex-1 min-w-0",
                canEdit && "relative pointer-events-none",
              )}
            >
              <GoalKeyResultItem
                goalArtifact={goalArtifact}
                keyResult={keyResult}
                isReadOnly={isReadOnly}
                onDelete={handleDeleteKeyResult}
              />
            </div>
          </div>
        ) : (
          <div
            className="hover:bg-gray-50 rounded-md -mx-2 pl-2 pr-3 py-1.5"
            key={keyResult.id}
          >
            <GoalKeyResultItem
              goalArtifact={goalArtifact}
              keyResult={keyResult}
              isReadOnly={isReadOnly}
              onDelete={handleDeleteKeyResult}
            />
          </div>
        ),
      )}
    </div>
  );

  return (
    <div className="w-full flex flex-col gap-2">
      <ConfirmationDialog />
      {editingKeyResult && (
        <GoalKeyResultModal
          keyResult={editingKeyResult}
          goalArtifact={goalArtifact}
          onClose={() => setEditingKeyResult(null)}
        />
      )}
      <div className="-mx-3 px-3 flex items-center justify-between">
        <div
          className={
            variant === "page" ? "tf-header-base-dark" : "text-sm font-medium"
          }
        >
          {title}
        </div>
        {action}
      </div>
      {items}
    </div>
  );
};

export default GoalKeyResultList;
